"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.favoritePeopleService = void 0;
/**
 * Favorite People Service
 *
 * Manages user's favorite actors/directors using Firestore for persistence.
 * Data is stored per-user in Firestore and syncs across devices.
 * Falls back to local storage for unauthenticated users or migration.
 */
const storageRegistry_1 = require("../platform/storageRegistry");
const firebase_1 = require("./firebase");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
function isCapacitorNativePlatform() {
    if (typeof window === 'undefined')
        return false;
    try {
        const Cap = window.Capacitor;
        return !!(Cap && typeof Cap.isNativePlatform === 'function' && Cap.isNativePlatform());
    }
    catch (_a) {
        return false;
    }
}
/**
 * JS Firebase Auth often has no currentUser on Capacitor while native Firebase Auth + Firestore are valid.
 * Unified/native Firestore reads still carry auth; rules enforce access.
 */
function canUseFirestoreForUserData() {
    try {
        if ((0, firebase_1.getFirebaseAuth)().currentUser)
            return true;
    }
    catch (_a) {
        /* ignore */
    }
    return isCapacitorNativePlatform();
}
class FavoritePeopleService {
    constructor() {
        this.STORAGE_KEY = 'favorite_people';
        this.COLLECTION_NAME = 'userFavoritePeople';
        this.MAX_FAVORITES = 50;
        this.listeners = new Map();
        // In-memory cache for reads
        this.cache = new Map();
    }
    subscribe(listener, userId) {
        this.listeners.set(listener, userId);
        return () => this.listeners.delete(listener);
    }
    notifyListeners(changedUserId) {
        this.listeners.forEach((subscribedUserId, listener) => {
            if (subscribedUserId === changedUserId || subscribedUserId === undefined) {
                this.getFavorites(subscribedUserId).then(favorites => {
                    listener(favorites);
                });
            }
        });
    }
    /** Clears in-memory favorites cache (e.g. after auth is ready) so reads are not stuck on stale empty. */
    clearInMemoryCache() {
        this.cache.clear();
    }
    getLocalStorageKey(userId) {
        return userId ? `${this.STORAGE_KEY}_${userId}` : this.STORAGE_KEY;
    }
    /**
     * Get favorites from Firestore (for authenticated users) or local storage (fallback)
     */
    getFavorites(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check in-memory cache first
            const cacheKey = userId || '__anonymous__';
            if (this.cache.has(cacheKey)) {
                return this.cache.get(cacheKey);
            }
            // If no userId, use local storage only
            if (!userId) {
                return this.getFromLocalStorage(undefined);
            }
            try {
                // Web: require JS auth. Capacitor: native Firestore/auth may be ready when JS currentUser is null.
                if (!canUseFirestoreForUserData()) {
                    const localOnly = yield this.getFromLocalStorage(userId);
                    // Do not cache empty while logged out: auth may still be hydrating on native;
                    // caching [] would skip the later Firestore read for this userId (e.g. other profiles).
                    if (localOnly.length > 0) {
                        this.cache.set(cacheKey, localOnly);
                    }
                    return localOnly;
                }
                // Try Firestore first
                console.log('📱 FavoritePeopleService: Fetching favorites from Firestore for user:', userId);
                const docSnapshot = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(this.COLLECTION_NAME, userId);
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    const favorites = data.favorites || [];
                    this.cache.set(cacheKey, favorites);
                    console.log('✅ FavoritePeopleService: Got', favorites.length, 'favorites from Firestore');
                    return favorites;
                }
                // No Firestore data - check local storage for migration
                console.log('📱 FavoritePeopleService: No Firestore data, checking local storage for migration');
                const localFavorites = yield this.getFromLocalStorage(userId);
                if (localFavorites.length > 0) {
                    // Migrate local data to Firestore
                    console.log('🔄 FavoritePeopleService: Migrating', localFavorites.length, 'favorites to Firestore');
                    yield this.saveToFirestore(userId, localFavorites);
                    // Clear local storage after successful migration
                    yield this.clearLocalStorage(userId);
                }
                this.cache.set(cacheKey, localFavorites);
                return localFavorites;
            }
            catch (error) {
                console.error('❌ FavoritePeopleService: Error fetching from Firestore, falling back to local:', error);
                // Fallback to local storage on error
                return this.getFromLocalStorage(userId);
            }
        });
    }
    /**
     * Get favorites from local storage (legacy/fallback)
     */
    getFromLocalStorage(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!(0, storageRegistry_1.hasStorage)())
                    return [];
                const key = this.getLocalStorageKey(userId);
                const stored = yield (0, storageRegistry_1.getStorage)().getItem(key);
                if (!stored)
                    return [];
                return JSON.parse(stored);
            }
            catch (error) {
                console.error('Error reading favorite people from local storage:', error);
                return [];
            }
        });
    }
    /**
     * Clear local storage (after migration)
     */
    clearLocalStorage(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!(0, storageRegistry_1.hasStorage)())
                    return;
                const key = this.getLocalStorageKey(userId);
                yield (0, storageRegistry_1.getStorage)().removeItem(key);
                console.log('✅ FavoritePeopleService: Cleared local storage after migration');
            }
            catch (error) {
                console.error('Error clearing local storage:', error);
            }
        });
    }
    /**
     * Save favorites to Firestore
     */
    saveToFirestore(userId, favorites) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let uid = null;
            try {
                uid = (_b = (_a = (0, firebase_1.getFirebaseAuth)().currentUser) === null || _a === void 0 ? void 0 : _a.uid) !== null && _b !== void 0 ? _b : null;
            }
            catch (_c) {
                uid = null;
            }
            const canWriteFirestore = (uid !== null && uid === userId) ||
                (!uid && isCapacitorNativePlatform());
            if (!canWriteFirestore) {
                if ((0, storageRegistry_1.hasStorage)()) {
                    const key = this.getLocalStorageKey(userId);
                    yield (0, storageRegistry_1.getStorage)().setItem(key, JSON.stringify(favorites));
                }
                return;
            }
            const data = {
                userId,
                favorites,
                updatedAt: Date.now()
            };
            yield (0, unifiedFirestoreService_1.unifiedSetDoc)(this.COLLECTION_NAME, userId, data);
            console.log('✅ FavoritePeopleService: Saved', favorites.length, 'favorites to Firestore');
        });
    }
    /**
     * Sync version for backward compat on web.
     * Returns cached data or empty array.
     */
    getFavoritesSync(userId) {
        const cacheKey = userId || '__anonymous__';
        // Return from cache if available
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        // Try sync storage if available (web only, for anonymous users)
        if (!userId) {
            try {
                if (!(0, storageRegistry_1.hasStorage)())
                    return [];
                const storage = (0, storageRegistry_1.getStorage)();
                if (storage.getItemSync) {
                    const stored = storage.getItemSync(this.getLocalStorageKey(userId));
                    if (!stored)
                        return [];
                    const parsed = JSON.parse(stored);
                    this.cache.set(cacheKey, parsed);
                    return parsed;
                }
            }
            catch (_a) {
                // ignore
            }
        }
        return [];
    }
    isFavorite(personId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const favorites = yield this.getFavorites(userId);
            return favorites.some(p => p.id === personId);
        });
    }
    isFavoriteSync(personId, userId) {
        const favorites = this.getFavoritesSync(userId);
        return favorites.some(p => p.id === personId);
    }
    addFavorite(person, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const favorites = yield this.getFavorites(userId);
                if (favorites.some(p => p.id === person.id))
                    return;
                favorites.unshift(Object.assign(Object.assign({}, person), { addedAt: Date.now() }));
                const trimmed = favorites.slice(0, this.MAX_FAVORITES);
                const cacheKey = userId || '__anonymous__';
                if (userId) {
                    // Save to Firestore for authenticated users
                    yield this.saveToFirestore(userId, trimmed);
                }
                else {
                    // Save to local storage for anonymous users
                    const key = this.getLocalStorageKey(userId);
                    yield (0, storageRegistry_1.getStorage)().setItem(key, JSON.stringify(trimmed));
                }
                this.cache.set(cacheKey, trimmed);
                this.notifyListeners(userId);
                console.log('✅ FavoritePeopleService: Added favorite:', person.name);
            }
            catch (error) {
                console.error('Error adding favorite person:', error);
            }
        });
    }
    removeFavorite(personId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const favorites = yield this.getFavorites(userId);
                const filtered = favorites.filter(p => p.id !== personId);
                const cacheKey = userId || '__anonymous__';
                if (userId) {
                    // Save to Firestore for authenticated users
                    yield this.saveToFirestore(userId, filtered);
                }
                else {
                    // Save to local storage for anonymous users
                    const key = this.getLocalStorageKey(userId);
                    yield (0, storageRegistry_1.getStorage)().setItem(key, JSON.stringify(filtered));
                }
                this.cache.set(cacheKey, filtered);
                this.notifyListeners(userId);
                console.log('✅ FavoritePeopleService: Removed favorite with id:', personId);
            }
            catch (error) {
                console.error('Error removing favorite person:', error);
            }
        });
    }
    toggleFavorite(person, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const isFav = yield this.isFavorite(person.id, userId);
            if (isFav) {
                yield this.removeFavorite(person.id, userId);
                return false;
            }
            else {
                yield this.addFavorite(person, userId);
                return true;
            }
        });
    }
    clearAll(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const cacheKey = userId || '__anonymous__';
            if (userId) {
                // Clear from Firestore
                yield this.saveToFirestore(userId, []);
            }
            else {
                // Clear from local storage
                const key = this.getLocalStorageKey(userId);
                yield (0, storageRegistry_1.getStorage)().removeItem(key);
            }
            this.cache.delete(cacheKey);
            this.notifyListeners(userId);
        });
    }
    /**
     * Migrate from local storage to Firestore
     * Called when user logs in to ensure their favorites are persisted
     */
    migrateToFirestore(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                console.log('🔄 FavoritePeopleService: Checking for local favorites to migrate...');
                // Check for favorites under the old anonymous key
                const anonymousKey = this.STORAGE_KEY;
                const userSpecificKey = this.getLocalStorageKey(userId);
                let localFavorites = [];
                if ((0, storageRegistry_1.hasStorage)()) {
                    // Check anonymous key first
                    const anonymousData = yield (0, storageRegistry_1.getStorage)().getItem(anonymousKey);
                    if (anonymousData) {
                        localFavorites = JSON.parse(anonymousData);
                        console.log('📦 Found', localFavorites.length, 'favorites under anonymous key');
                    }
                    // Also check user-specific key
                    const userData = yield (0, storageRegistry_1.getStorage)().getItem(userSpecificKey);
                    if (userData) {
                        const userFavorites = JSON.parse(userData);
                        // Merge, avoiding duplicates
                        for (const fav of userFavorites) {
                            if (!localFavorites.some(f => f.id === fav.id)) {
                                localFavorites.push(fav);
                            }
                        }
                        console.log('📦 Merged user-specific favorites, total:', localFavorites.length);
                    }
                }
                if (localFavorites.length > 0) {
                    // Check if Firestore already has data
                    const docSnapshot = yield (0, unifiedFirestoreService_1.unifiedGetDoc)(this.COLLECTION_NAME, userId);
                    if (docSnapshot.exists()) {
                        // Merge with existing Firestore data
                        const firestoreData = docSnapshot.data();
                        const existingFavorites = firestoreData.favorites || [];
                        for (const fav of localFavorites) {
                            if (!existingFavorites.some(f => f.id === fav.id)) {
                                existingFavorites.push(fav);
                            }
                        }
                        // Trim and save
                        const merged = existingFavorites.slice(0, this.MAX_FAVORITES);
                        yield this.saveToFirestore(userId, merged);
                        console.log('✅ Merged local favorites with Firestore, total:', merged.length);
                    }
                    else {
                        // Just save local favorites to Firestore
                        const trimmed = localFavorites.slice(0, this.MAX_FAVORITES);
                        yield this.saveToFirestore(userId, trimmed);
                        console.log('✅ Migrated', trimmed.length, 'favorites to Firestore');
                    }
                    // Clear local storage after successful migration
                    if ((0, storageRegistry_1.hasStorage)()) {
                        yield (0, storageRegistry_1.getStorage)().removeItem(anonymousKey);
                        yield (0, storageRegistry_1.getStorage)().removeItem(userSpecificKey);
                        console.log('✅ Cleared local storage after migration');
                    }
                    // Clear cache to force refresh
                    this.cache.delete(userId);
                    this.cache.delete('__anonymous__');
                }
                else {
                    console.log('📭 No local favorites to migrate');
                }
            }
            catch (error) {
                console.error('❌ Error migrating favorite people to Firestore:', error);
            }
        });
    }
    /**
     * @deprecated Use migrateToFirestore instead
     */
    migrateToUserSpecificKey(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Redirect to new migration method
            yield this.migrateToFirestore(userId);
        });
    }
}
exports.favoritePeopleService = new FavoritePeopleService();
